import Flex from '@Resources/styles/flex.scss';
import { splitCamelCase } from '@Utils/helpers/stringHelpers';
import { useAuth0 } from '@auth0/auth0-react';
import cx from 'classnames';
import _cloneDeep from 'lodash/cloneDeep';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalFooter from '@Components/ModalComponents/ModalFooter';
import FoldersApi from '@Data/folders/Api';
import { IFolder } from '@Data/folders/Interfaces';
import { folderFieldsState } from '@Data/folders/Reducer';
import NotificationsApi from '@Data/notifications/Api';
import {
    NotificationSeverity,
    NotificationType,
} from '@Data/notifications/Interfaces';
import ProcessesApi from '@Data/processes/Api';
import { tryCreateProcessNotificationForTask } from '@Data/processes/Helpers';
import { IProcess, IRunningProcess } from '@Data/processes/Interfaces';
import {
    OLDPayloadDataState,
    proccessPayloadInputsState,
    selectedProcessState,
} from '@Data/processes/Reducer';
import { getParsedAuth0Username } from '@Services/authentication/Helpers';
import AutomationTaskApi from '@Services/iiq/Api';
import { IAutomationTask } from '@Services/iiq/Interfaces';
import {
    automationTasksState,
    selectedAutomationTaskUriState,
} from '@Services/iiq/Reducer';
import { success } from '@Services/notification/NotificationServices';
import ProcessPayloadForm from './ProcessPayloadForm';
import ProcessSelector from './ProcessSelector';
import Styles from './Processes.scss';
import OLDProcessPayloadForm from './deprecated/OLDProcessPayloadForm';

const ProcessesDialogue = (props) => {
    const {
        projectId,
        onCancel,
        onComplete,
        setCancelDisabled,
        selectedFolder,
        setFooter,
    } = props;

    const selectedProcess: IProcess = useSelector(selectedProcessState);
    const folderFields: Array<string> = useSelector(folderFieldsState);
    const automationTasks: Array<IAutomationTask> =
        useSelector(automationTasksState);
    const selectedAutomationTaskUri: string = useSelector(
        selectedAutomationTaskUriState
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);
    const payloadInputs = useSelector(proccessPayloadInputsState);
    const { user } = useAuth0();
    const payloadData = useSelector(OLDPayloadDataState);

    const handleSelectProcess = (process: IProcess) => {
        ProcessesApi.setProcess(process, () => {
            if (selectedFolder) {
                ProcessesApi.OLDinitialisePayloadData(
                    process.process_payload_schema,
                    projectId,
                    selectedFolder.folder_id
                );
                setCanSubmit(true);

                if (
                    process.process_identifier_name == 'run_iiq_automation_task'
                ) {
                    AutomationTaskApi.getAutomationTasks();
                } else {
                    FoldersApi.getFolderFields(selectedFolder.folder_id);
                }
            }
        });
    };

    const handleClearProcess = () => {
        ProcessesApi.setProcess(null);
    };

    const resetState = () => {
        ProcessesApi.setProcess(null);
        setIsSubmitting(false);
    };

    const handleCancel = () => {
        FoldersApi.clearFolderFields();
        onCancel();
    };

    const handleComplete = () => {
        setIsSubmitting(true);
        setCancelDisabled(true);
        ProcessesApi.runProcess(
            selectedProcess,
            payloadData,
            projectId,
            getParsedAuth0Username(user.sub),
            (runningProcess: IRunningProcess) => {
                tryCreateProcessNotificationForTask(
                    runningProcess.process_name,
                    runningProcess.task_id
                );
                resetState();
                onComplete();
            }
        );
    };

    useEffect(() => {
        setFooter(
            <ModalFooter
                id="process-footer"
                onCancel={handleCancel}
                onSubmit={handleComplete}
                hideSubmit={!selectedProcess || !selectedFolder}
                canSubmit={canSubmit}
                isSubmitting={isSubmitting}
            />
        );
    }, [selectedProcess, selectedFolder, isSubmitting, canSubmit, payloadData]);

    return (
        <div>
            <div className={cx(Flex.flex, Flex.row, Flex.justifyBetween)}>
                <div className={Styles.spFolderLabelContainer}>
                    <ProcessSelector
                        selectedProcess={selectedProcess}
                        onSelectProcess={handleSelectProcess}
                        handleClear={handleClearProcess}
                    />
                </div>

                <div
                    id="process-info-container"
                    className={cx(Styles.spProcessInfo, Styles.spLeftMargin)}
                >
                    {selectedProcess && (
                        <React.Fragment>
                            <span>About this tool</span>
                            <p>{selectedProcess.description}</p>
                        </React.Fragment>
                    )}
                </div>
            </div>

            {selectedProcess && selectedFolder && (
                <OLDProcessPayloadForm
                    payloadData={payloadData}
                    projectId={projectId}
                    folderId={selectedFolder.folder_id}
                    folderFields={folderFields}
                    automationTasks={automationTasks}
                    selectedAutomationTaskUri={selectedAutomationTaskUri}
                    onSubmit={handleComplete}
                    onCancel={onCancel}
                />
            )}
        </div>
    );
};

ProcessesDialogue.propTypes = {
    projectId: PropTypes.number,
    onCancel: PropTypes.func,
    setCancelDisabled: PropTypes.func,
    onComplete: PropTypes.func,
    selectedFolder: PropTypes.any,
    setFooter: PropTypes.func,
};
export default ProcessesDialogue;
